import {
  ExecutorExternalFormItems,
  ExecutorJobsExternalFormItems,
  ExecutorJobsExternalInitialFormItems,
  ExecutorJobsLocalFormItems,
  ExecutorJobsLocalInitialFormItems,
  ExecutorJobsMaximumLimitFormItems,
  ExecutorLocalFormItems,
} from "@via/pages/account/profile/run-environments/run-environment-form/run-environment-constants.ts";
import { JSX } from "react";

export function RunEnvironmentInput({
  register,
  registerValue,
  title,
  placeholderText,
}): JSX.Element {
  return (
    <div key={title}>
      <label className="block text-sm font-medium leading-6 text-gray-900">
        {title}
      </label>
      <div className="mt-2">
        <input
          {...register(registerValue)}
          type="text"
          className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
          placeholder={placeholderText}
        />
      </div>
    </div>
  );
}

export function ExecutorLocal({ register }): JSX.Element {
  return (
    <div className="mt-2 flex gap-2">
      {ExecutorLocalFormItems.map(formItem => (
        <RunEnvironmentInput
          key={formItem.registerValue}
          register={register}
          registerValue={formItem.registerValue}
          title={formItem.title}
          placeholderText={formItem.placeholderText}
        />
      ))}
    </div>
  );
}

export function ExecutorExternals({ register, selectedExecutor }): JSX.Element {
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {ExecutorExternalFormItems.map(formItem => (
        <RunEnvironmentInput
          key={formItem.registerValue}
          register={register}
          registerValue={formItem.registerValue}
          title={
            formItem.title === "Queue" && selectedExecutor === "slurm"
              ? "Partition"
              : formItem.title
          }
          placeholderText={
            formItem.title === "Queue" && selectedExecutor === "slurm"
              ? "Your Partition"
              : formItem.placeholderText
          }
        />
      ))}
    </div>
  );
}

export function ExecutorJobsLocal({
  register,
  selectedExecutorJobs,
}): JSX.Element {
  return (
    <div>
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Executor Settings for Nextflow Jobs
        </label>

        <div className="mt-2 flex flex-wrap gap-2">
          {ExecutorJobsLocalFormItems.map(formItem => (
            <RunEnvironmentInput
              key={formItem.registerValue}
              register={register}
              registerValue={formItem.registerValue}
              title={formItem.title}
              placeholderText={formItem.placeholderText}
            />
          ))}

          {selectedExecutorJobs === "ignite" ? (
            <RunEnvironmentInput
              register={register}
              registerValue={"jobCluOpt"}
              title={"Other Options"}
              placeholderText={"Your Other Options"}
            />
          ) : selectedExecutorJobs === "google-batch" ? (
            <RunEnvironmentInput
              register={register}
              registerValue={"jobTime"}
              title={"Time(min.)"}
              placeholderText={"Your Time(min.)"}
            />
          ) : null}
        </div>
      </div>

      <div className="mt-4">
        <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
          <input
            {...register("checkInitExec")}
            type="checkbox"
            className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
          Use Custom Initial Run Settings
        </label>

        <div className="mt-2 flex flex-wrap gap-2">
          {ExecutorJobsLocalInitialFormItems.map(formItem => (
            <RunEnvironmentInput
              key={formItem.registerValue}
              register={register}
              registerValue={formItem.registerValue}
              title={formItem.title}
              placeholderText={formItem.placeholderText}
            />
          ))}

          {selectedExecutorJobs === "ignite" ? (
            <RunEnvironmentInput
              register={register}
              registerValue={"initJobCluOpt"}
              title={"Other Options"}
              placeholderText={"Your Other Options"}
            />
          ) : selectedExecutorJobs === "google-batch" ? (
            <RunEnvironmentInput
              register={register}
              registerValue={"initJobTime"}
              title={"Time(min.)"}
              placeholderText={"Your Time(min.)"}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function ExecutorJobsExternal({
  register,
  selectedExecutorJobs,
}): JSX.Element {
  return (
    <div>
      <div>
        <label className="block text-sm font-medium leading-6 text-gray-900">
          Executor Settings for Nextflow Jobs
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {ExecutorJobsExternalFormItems.map(formItem => (
            <RunEnvironmentInput
              key={formItem.registerValue}
              register={register}
              registerValue={formItem.registerValue}
              title={
                formItem.title === "Queue" && selectedExecutorJobs === "slurm"
                  ? "Partition"
                  : formItem.title
              }
              placeholderText={
                formItem.title === "Queue" && selectedExecutorJobs === "slurm"
                  ? "Your Partition"
                  : formItem.placeholderText
              }
            />
          ))}
        </div>
      </div>

      <div className="mt-4">
        <label className="flex items-center text-sm font-medium leading-6 text-gray-900">
          <input
            {...register("checkInitExec")}
            type="checkbox"
            className="mr-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
          />
          Use Custom Initial Run Settings
        </label>

        <div className="mt-2 flex flex-wrap gap-2">
          {ExecutorJobsExternalInitialFormItems.map(formItem => (
            <RunEnvironmentInput
              key={formItem.registerValue}
              register={register}
              registerValue={formItem.registerValue}
              title={
                formItem.title === "Queue" && selectedExecutorJobs === "slurm"
                  ? "Partition"
                  : formItem.title
              }
              placeholderText={
                formItem.title === "Queue" && selectedExecutorJobs === "slurm"
                  ? "Your Partition"
                  : formItem.placeholderText
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export function MaximumExecutorJobLimit({ register }): JSX.Element {
  return (
    <div>
      <label className="block text-sm font-medium leading-6 text-gray-900">
        Maximum Limit for Nextflow Jobs
      </label>

      <div className="mt-2 flex gap-2">
        {ExecutorJobsMaximumLimitFormItems.map(formItem => (
          <RunEnvironmentInput
            key={formItem.registerValue}
            register={register}
            registerValue={formItem.registerValue}
            title={formItem.title}
            placeholderText={formItem.placeholderText}
          />
        ))}
      </div>
    </div>
  );
}
